import { createTheme, Theme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

// Clean, minimalist design system inspired by HabitKit
export const getTheme = (mode: ThemeMode): Theme => createTheme({
  palette: {
    mode,
    primary: {
      main: '#36C65A',
      dark: '#2BA84A',
      light: '#5ED67D',
      contrastText: '#ffffff',
    },
    secondary: {
      main: mode === 'light' ? '#1a1a1a' : '#202332',
      light: mode === 'light' ? '#525252' : '#2a2d42',
      dark: mode === 'light' ? '#000000' : '#18192a',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'light' ? '#ffffff' : '#141424',
      paper: mode === 'light' ? '#f8f9fa' : '#202332',
    },
    text: {
      primary: mode === 'light' ? '#000000' : '#ffffff',
      secondary: mode === 'light' ? '#555555' : '#a0a0a0',
    },
    grey: {
      50: mode === 'light' ? '#f8f9fa' : '#2a2d42',
      100: mode === 'light' ? '#f1f3f5' : '#323550',
      200: mode === 'light' ? '#e9ecef' : '#3a3d5a',
      300: mode === 'light' ? '#dee2e6' : '#424564',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: {
      fontSize: '4rem',
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      '@media (max-width:900px)': {
        fontSize: '3rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2.25rem',
      },
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      '@media (max-width:900px)': {
        fontSize: '2rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    body1: {
      fontSize: '1.125rem',
      lineHeight: 1.7,
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '1rem',
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '14px 32px',
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#2BA84A',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(20, 20, 36, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width:1200px)': {
            maxWidth: '1200px',
          },
        },
      },
    },
  },
});

// Default light theme for backwards compatibility
export const theme = getTheme('light');
